import { useEffect, useRef, useState } from "react";
import { Loader2, Package } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { newKey, processPackage, sendPackage } from "@/lib/imports";
import { useDraft } from "@/components/import/DraftContext";

function Stat({ label, value, tone = "text-amber-100", testid }) {
  return (
    <div className="rounded-lg border border-[#2A3447] bg-black/20 px-4 py-3" data-testid={testid}>
      <p className={`font-heading text-2xl ${tone}`}>{value}</p>
      <p className="text-xs text-slate-500">{label}</p>
    </div>
  );
}

function PackageProgress() {
  const { draft, id, refresh } = useDraft();
  const pkg = draft.package;
  const [busy, setBusy] = useState(false);
  const started = useRef(false);
  const run = async () => {
    setBusy(true);
    try {
      await processPackage(id, () => refresh());
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
      refresh();
    }
  };
  useEffect(() => {
    if (pkg && ["pending", "processing"].includes(pkg.status) && !started.current) {
      started.current = true;
      run();
    }
  }); // eslint-disable-line react-hooks/exhaustive-deps
  const reupload = async (file) => {
    setBusy(true);
    try {
      const uploadId = await sendPackage(file);
      await api.post(`/imports/${id}/package`, { upload_id: uploadId, request_key: newKey() });
      started.current = false;
      refresh();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };
  if (!pkg || (pkg.status === "done" && !pkg.errors?.length)) return null;
  const pct = pkg.total ? Math.round((pkg.processed / pkg.total) * 100) : 100;
  return (
    <div className="panel space-y-2 p-4 text-sm" data-testid="import-package-status">
      <p className="flex items-center gap-2 text-slate-200">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Package className="h-4 w-4 text-amber-300" />}
        Package images: {pkg.processed}/{pkg.total} processed {pkg.status === "done" ? "· done" : ""}</p>
      {pkg.status !== "done" && <div className="h-1.5 overflow-hidden rounded-full bg-[#2A3447]"><div className="h-full bg-amber-500 transition-[width]" style={{ width: `${pct}%` }} /></div>}
      {["lost", "failed"].includes(pkg.status) && (
        <div className="flex flex-wrap items-center gap-3 text-amber-200">
          <span>{pkg.message || "The uploaded package is no longer on the server. Upload the same ZIP again to continue; finished images are kept."}</span>
          {pkg.status === "lost" && (
            <label className="btn-outline cursor-pointer px-3 py-1.5 text-xs" data-testid="import-package-reupload">Upload ZIP again
              <input type="file" accept=".zip" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; f && reupload(f); }} />
            </label>
          )}
        </div>
      )}
      {pkg.errors?.length > 0 && (
        <details className="text-xs text-slate-400"><summary className="cursor-pointer" data-testid="import-package-notes">{pkg.errors.length} package note(s)</summary>
          <ul className="mt-2 space-y-1">{pkg.errors.slice(0, 50).map((e, i) => <li key={i}>{e}</li>)}</ul></details>
      )}
    </div>
  );
}

export function DraftSummary() {
  const { draft } = useDraft();
  const s = draft.summary;
  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-300" data-testid="import-images-counter"><strong className="text-amber-200">{s.images_complete} of {s.total}</strong> cards have all required face images.</p>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        <Stat label="Cards found" value={s.total} testid="import-stat-total" />
        <Stat label="Valid" value={s.valid} tone="text-emerald-300" testid="import-stat-valid" />
        <Stat label="Need fixes" value={s.errors} tone={s.errors ? "text-red-300" : "text-slate-400"} testid="import-stat-errors" />
        <Stat label="Need images" value={s.missing_images} tone={s.missing_images ? "text-amber-300" : "text-slate-400"} testid="import-stat-missing" />
        <Stat label="Already exist" value={s.existing} tone="text-copper-200" testid="import-stat-existing" />
        <Stat label="Imported" value={s.finalized} tone="text-aether-300" testid="import-stat-finalized" />
      </div>
      {draft.warnings?.length > 0 && <p className="text-xs text-amber-300/80" data-testid="import-draft-warnings">{draft.warnings.join(" · ")}</p>}
      <PackageProgress />
    </div>
  );
}
